import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import { toast } from 'sonner';
import { Sparkles, Loader2, RefreshCw, Copy, Check, Wand2, Type, MessageSquare, Zap, FlaskConical } from 'lucide-react';
import { aiContentAPI, abTestingAPI } from '../api';

const GENERATION_TYPES = [
  { value: 'headline', label: 'Headlines', icon: Type, description: 'Generate compelling headlines' },
  { value: 'tagline', label: 'Taglines', icon: MessageSquare, description: 'Create memorable taglines' },
  { value: 'cta', label: 'Call-to-Actions', icon: Zap, description: 'Generate CTAs' },
  { value: 'expand', label: 'Expand Content', icon: Wand2, description: 'Expand bullet points into copy' },
  { value: 'tone_adjust', label: 'Adjust Tone', icon: RefreshCw, description: 'Rewrite in different tone' },
  { value: 'full_copy', label: 'Full Copy', icon: Sparkles, description: 'Generate complete marketing copy' }
];

const TONE_OPTIONS = [
  'professional', 'casual', 'friendly', 'authoritative', 
  'innovative', 'empathetic', 'urgent', 'luxurious'
];

export const AIContentGenerator = ({ 
  open, 
  onOpenChange, 
  onSelectContent,
  initialType = 'headline',
  context = {}
}) => {
  const [generationType, setGenerationType] = useState(initialType);
  const [inputText, setInputText] = useState('');
  const [targetTone, setTargetTone] = useState('');
  const [numVariations, setNumVariations] = useState(3);
  const [maxLength, setMaxLength] = useState(100);
  const [generating, setGenerating] = useState(false);
  const [variations, setVariations] = useState([]);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [creatingABTest, setCreatingABTest] = useState(false);

  const handleGenerate = async () => {
    if (!inputText.trim()) {
      toast.error('Please enter some input text or context');
      return;
    }

    setGenerating(true);
    setVariations([]);

    try {
      const res = await aiContentAPI.generate({
        generation_type: generationType,
        input_text: inputText,
        context,
        target_tone: targetTone || undefined,
        max_length: maxLength,
        num_variations: numVariations
      });

      setVariations(res.data.variations || []);
      toast.success(`Generated ${res.data.variations?.length || 0} variations`);
    } catch (error) {
      console.error('AI generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
    toast.success('Copied to clipboard');
  };

  const handleUse = (text) => {
    if (onSelectContent) {
      onSelectContent(text, generationType);
    }
    onOpenChange(false);
  };

  const handleCreateABTest = async () => {
    if (variations.length < 2) {
      toast.error('Need at least 2 variations for A/B testing');
      return;
    }

    setCreatingABTest(true);
    try {
      const testData = {
        name: `AI ${generationType} Test - ${new Date().toLocaleDateString()}`,
        description: `Testing AI-generated ${generationType} variations`,
        content_type: generationType === 'full_copy' ? 'full_copy' : generationType === 'tone_adjust' ? 'body_copy' : generationType,
        variations: variations.map((content, idx) => ({
          content,
          label: idx === 0 ? 'Control' : `Variation ${String.fromCharCode(65 + idx)}`,
          is_control: idx === 0
        })),
        target_metric: 'conversion_rate',
        minimum_sample_size: 100,
        ai_generated: true,
        generation_context: inputText
      };

      await abTestingAPI.create(testData);
      toast.success('A/B test created! View it in the A/B Testing page');
    } catch (error) {
      console.error('Failed to create A/B test:', error);
      toast.error('Failed to create A/B test');
    } finally {
      setCreatingABTest(false);
    }
  };

  const getMaxLengthDefault = () => {
    switch (generationType) {
      case 'headline': return 100;
      case 'tagline': return 50;
      case 'cta': return 30;
      case 'expand': return 300;
      case 'tone_adjust': return 500;
      case 'full_copy': return 500;
      default: return 100;
    }
  };

  const handleTypeChange = (type) => {
    setGenerationType(type);
    setMaxLength(getMaxLengthDefault());
    setVariations([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            AI Content Generator
          </DialogTitle>
          <DialogDescription>
            Generate marketing content powered by your brand voice settings
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6 py-4">
          {/* Left side - Input */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Generation Type</Label>
              <div className="grid grid-cols-2 gap-2">
                {GENERATION_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <Button
                      key={type.value}
                      variant={generationType === type.value ? 'default' : 'outline'}
                      className="justify-start h-auto py-2"
                      onClick={() => handleTypeChange(type.value)}
                    >
                      <Icon className="h-4 w-4 mr-2" />
                      <div className="text-left">
                        <p className="text-sm font-medium">{type.label}</p>
                      </div>
                    </Button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                {generationType === 'expand' ? 'Content to Expand' :
                 generationType === 'tone_adjust' ? 'Content to Rewrite' :
                 'Topic / Context'}
              </Label>
              <Textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder={
                  generationType === 'expand' ? 'Enter bullet points or brief notes...' :
                  generationType === 'tone_adjust' ? 'Enter content to rewrite...' :
                  'Describe what you want to generate content about...'
                }
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Target Tone (optional)</Label>
                <Select value={targetTone || "default"} onValueChange={(v) => setTargetTone(v === "default" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Use brand default" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Use brand default</SelectItem>
                    {TONE_OPTIONS.map(tone => (
                      <SelectItem key={tone} value={tone} className="capitalize">
                        {tone}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Variations</Label>
                <Select value={String(numVariations)} onValueChange={(v) => setNumVariations(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map(n => (
                      <SelectItem key={n} value={String(n)}>{n} variation{n > 1 ? 's' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Max Length (characters)</Label>
              <Input
                type="number"
                value={maxLength}
                onChange={(e) => setMaxLength(parseInt(e.target.value) || 100)}
                min={10}
                max={2000}
              />
            </div>

            <Button 
              className="w-full" 
              onClick={handleGenerate}
              disabled={generating || !inputText.trim()}
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Content
                </>
              )}
            </Button>
          </div>

          {/* Right side - Results */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Generated Variations</Label>
              {variations.length > 0 && (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{variations.length} results</Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCreateABTest}
                    disabled={creatingABTest || variations.length < 2}
                    data-testid="create-ab-test-btn"
                  >
                    {creatingABTest ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <FlaskConical className="h-3 w-3 mr-1" />
                    )}
                    A/B Test
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
              {generating ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-purple-600" />
                    <p className="text-sm text-muted-foreground">Generating with AI...</p>
                  </div>
                </div>
              ) : variations.length === 0 ? (
                <div className="flex items-center justify-center py-12 border-2 border-dashed rounded-lg">
                  <div className="text-center text-muted-foreground">
                    <Sparkles className="h-10 w-10 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">Generated content will appear here</p>
                  </div>
                </div>
              ) : (
                variations.map((text, idx) => (
                  <div 
                    key={idx} 
                    className="p-4 border rounded-lg hover:border-purple-300 transition-colors group"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <Badge variant="outline" className="text-xs">#{idx + 1}</Badge>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopy(text, idx)}
                        >
                          {copiedIndex === idx ? (
                            <Check className="h-3 w-3 text-green-500" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleUse(text)}
                        >
                          Use This
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{text}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AIContentGenerator;
